import { Field, useFormikContext } from 'formik';
import { clsx } from 'clsx';
import { useState } from 'react';
import { Chip } from '../../../chip/Chip';
import { SyncFrequencyOption } from '../../../shared/interfaces/integration';
import styles from './SyncFrequencyFormField.module.scss';
import { useAnalyticsTracker } from '../../../tracking/hooks/useAnalyticsTracker';
import { PipelineSyncFrequencyTrackers } from '../../tracking/pipeline-sync-frequency-trackers';
import { SyncFrequenciesFeatureOptions } from '../../interfaces/pipeline-settings-config';
import { getPredefinedSyncFrequencyOptionFromMillis } from './utils';
import EntityBanner from '../../../shared/components/EntityBanner/EntityBanner';
import { FormikTextField } from '../../../input/FormikTextField/FormikTextField';
import { millisToHours, hoursToMillis } from '../../../shared/utils/date';

export interface SyncFrequencyFormFieldProps {
  disabled?: boolean;
  className?: string;
  customFieldClassName?: string;
  fieldName: string;
  featureOptions: SyncFrequenciesFeatureOptions;
}

export function SyncFrequencyFormField({
  disabled,
  className,
  fieldName,
  featureOptions,
  customFieldClassName
}: SyncFrequencyFormFieldProps) {
  const { eventTrack } = useAnalyticsTracker();

  const formikProps = useFormikContext<{ [key: string]: number }>();

  const currentValueInMillis = formikProps.values[fieldName];

  const [isCurrentValueCustom, setIsCurrentValueCustom] = useState(
    !getPredefinedSyncFrequencyOptionFromMillis(featureOptions.options, currentValueInMillis)
  );

  const onPredefinedChipClick = (frequency: SyncFrequencyOption) => {
    eventTrack({
      action:
        PipelineSyncFrequencyTrackers.PIPELINE_TRACKER_PIPELINE_SETTINGS_SYNC_FREQUENCY_PREDEFINED_CHIP_CLICK,
      properties: {
        frequency: frequency.label
      }
    });

    setIsCurrentValueCustom(false);

    (async () => {
      await formikProps.setFieldValue(fieldName, frequency.value);
    })();
  };

  const onCustomChipClick = () => {
    eventTrack({
      action:
        PipelineSyncFrequencyTrackers.PIPELINE_TRACKER_PIPELINE_SETTINGS_SYNC_FREQUENCY_CUSTOM_CHIP_CLICK
    });

    setIsCurrentValueCustom(true);

    (async () => {
      await formikProps.setFieldValue(fieldName, featureOptions.default_value);
    })();
  };

  return (
    <>
      <div className={clsx('text-bold w-100', styles.syncFrequencyFormContainer, className)}>
        {featureOptions.options.map((frequency, index) => (
          <Chip
            key={frequency.label}
            dataId={`chip-${index}`}
            size='large'
            label={frequency.label}
            selected={!isCurrentValueCustom && frequency.value === currentValueInMillis}
            disabled={disabled}
            onClick={() => onPredefinedChipClick(frequency)}
          />
        ))}

        {featureOptions.custom_option ? (
          <Chip
            dataId='chip-custom'
            size='large'
            selected={isCurrentValueCustom}
            disabled={disabled}
            label='Custom'
            onClick={() => onCustomChipClick()}
          />
        ) : null}
      </div>

      {isCurrentValueCustom ? (
        <Field
          name={fieldName}
          dataId='custom-frequency-hours'
          label='Hours'
          placeholder='Hours'
          className={customFieldClassName}
          type='number'
          sx={{ width: '92px' }}
          disabled={disabled}
          disableDecimal
          required
          component={FormikTextField}
          convertFrom={millisToHours}
          convertTo={hoursToMillis}
          errorMessageClassName={styles.hourTextFieldHelperText}
          helperText={
            <span className={styles.hourTextFieldHelperText}>
              {`Specify sync frequency in range of ${featureOptions.custom_option.min} to ${
                featureOptions.custom_option.max
              } ${featureOptions.custom_option.time_unit.toLowerCase()}`}
              ,
            </span>
          }
        />
      ) : null}

      {featureOptions.banner ? (
        <EntityBanner
          dataId='sync-freq'
          content={featureOptions.banner?.text}
          type={featureOptions.banner?.type}
        />
      ) : null}
    </>
  );
}
